<template>
  <form class="challenge-campaign-form">
    <div class="category-top-bar sticky-header">
      <div class="top-bar">
        <p>
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" @click.prevent="backButton">
            <i class="ti-arrow-left"></i> {{ $t('general.back') }}
          </a>
        </p>
        <p class="d-inline-flex">
          <a class="btn btn-danger btn-icon btn mar-rt-10 delete" v-if="id" @click.prevent="deleteCampaign">
            <img src="/static/img/icons/icon-close.png"> {{ $t('general.delete') }}
          </a>
          <button type="submit" class="btn btn-success btn-icon mar-rt-10 save" @click.prevent="validate" :title="$t('general.save')">
            <img src="/static/img/icons/save.png"> {{ $t('general.save') }}
          </button>
        </p>
      </div>
    </div>

    <div v-if="loading" class="show-loader challenge-campaign-form__loader">
      <div class="el-loading-spinner">
        <svg viewBox="25 25 50 50" class="circular">
          <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
        </svg>
      </div>
    </div>

    <div class="row mar-to-15 mar-bt-15">
      <div class="col-md-12">
        <div class="card">
          <div class="card-content">
            <div class="header challenge-campaign-form__section-title">
              <h5>{{ $t('challengeCampaigns.details') }}</h5>
            </div>

            <div class="row">
              <div class="col-md-8">
                <div class="form-group">
                  <label>{{ $t('challengeCampaigns.title') }}</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="model.title"
                    v-validate="'required|min:2'"
                    name="title"
                    :placeholder="$t('challengeCampaigns.titlePlaceholder')"
                  >
                  <small class="text-danger" v-if="errors.has('title')">{{ errors.first('title') }}</small>
                </div>
                <div class="form-group">
                  <label>{{ $t('challengeCampaigns.description') }}</label>
                  <textarea
                    rows="3"
                    class="form-control"
                    v-model="model.description"
                    :placeholder="$t('challengeCampaigns.descriptionPlaceholder')"
                  ></textarea>
                </div>
                <div class="form-group challenge-campaign-form__active">
                  <p-switch v-model="model.active" type="success" />
                  <label>{{ $t('general.active') }}</label>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label>{{ $t('challengeCampaigns.image') }}</label>
                  <el-upload
                    ref="upload"
                    action=""
                    list-type="picture-card"
                    :auto-upload="false"
                    :limit="1"
                    :file-list="model.imagelist"
                    :on-change="handleImgChange"
                    :on-remove="handleRemove"
                    :on-exceed="handleExceed">
                    <i class="el-icon-plus"></i>
                  </el-upload>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card mar-to-15">
          <div class="card-content">
            <div class="header challenge-campaign-form__section-title">
              <h5>{{ $t('challengeCampaigns.conditions') }}</h5>
            </div>
            <ul class="challenge-campaign-form__info-list">
              <li>{{ $t('challengeCampaigns.conditionsAndInfo') }}</li>
              <li>{{ $t('challengeCampaigns.deliveredOnlyInfo') }}</li>
            </ul>

            <h6 class="challenge-campaign-form__group-title">{{ $t('challengeCampaigns.goalGroup') }}</h6>
            <div class="row">
              <div class="col-md-4">
                <div class="form-group">
                  <label>{{ $t('challengeCampaigns.target') }}</label>
                  <input type="number" min="1" class="form-control" v-model.number="model.target">
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label>{{ $t('challengeCampaigns.periodOfTimeDays') }}</label>
                  <input type="number" min="1" class="form-control" v-model.number="model.period_of_time_days" :placeholder="$t('challengeCampaigns.periodOfTimeDaysHint')">
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group challenge-campaign-form__switch-field">
                  <label>{{ $t('challengeCampaigns.distinctOrder') }}</label>
                  <div class="challenge-campaign-form__active">
                    <p-switch v-model="model.distinct_order" type="success" />
                  </div>
                  <small class="text-muted">{{ $t('challengeCampaigns.distinctOrderHint') }}</small>
                </div>
              </div>
            </div>

            <h6 class="challenge-campaign-form__group-title">{{ $t('challengeCampaigns.orderFiltersGroup') }}</h6>
            <div class="row">
              <div class="col-md-3">
                <div class="form-group">
                  <label>{{ $t('challengeCampaigns.minOrderValue') }}</label>
                  <input type="number" min="0" step="0.01" class="form-control" v-model="model.min_order_value">
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>{{ $t('challengeCampaigns.locations') }}</label>
                  <el-select
                    v-model="locationIds"
                    multiple
                    collapse-tags
                    clearable
                    filterable
                    :placeholder="$t('challengeCampaigns.leaveEmptyAll')"
                    style="width: 100%;"
                  >
                    <el-option
                      v-for="loc in locations"
                      :key="loc.id"
                      :label="loc.name"
                      :value="loc.id"
                    />
                  </el-select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>{{ $t('challengeCampaigns.deliveryMethods') }}</label>
                  <el-select
                    v-model="model.delivery_methods"
                    multiple
                    collapse-tags
                    clearable
                    filterable
                    :placeholder="$t('challengeCampaigns.leaveEmptyAll')"
                    style="width: 100%;"
                  >
                    <el-option
                      v-for="m in deliveryMethodOptions"
                      :key="m"
                      :label="$t('deliveryTypes.' + m)"
                      :value="m"
                    />
                  </el-select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>{{ $t('challengeCampaigns.sources') }}</label>
                  <el-select
                    v-model="model.order_source_ids"
                    multiple
                    collapse-tags
                    clearable
                    filterable
                    :placeholder="$t('challengeCampaigns.leaveEmptyAll')"
                    style="width: 100%;"
                  >
                    <el-option v-for="s in sourceOptions" :key="s.id" :label="s.label" :value="s.id" />
                  </el-select>
                </div>
              </div>
            </div>

            <h6 class="challenge-campaign-form__group-title">{{ $t('challengeCampaigns.scheduleGroup') }}</h6>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>{{ $t('challengeCampaigns.timePeriod') }}</label>
                  <el-date-picker
                    v-model="model.fromto"
                    type="datetimerange"
                    format="yyyy-MM-dd HH:mm"
                    value-format="yyyy-MM-dd HH:mm:ss"
                    style="width: 100%;"
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>{{ $t('challengeCampaigns.days') }}</label>
                  <el-select
                    v-model="model.days"
                    multiple
                    collapse-tags
                    clearable
                    :placeholder="$t('challengeCampaigns.leaveEmptyAll')"
                    style="width: 100%;"
                  >
                    <el-option v-for="d in dayOptions" :key="d.id" :label="d.label" :value="d.id" />
                  </el-select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>{{ $t('challengeCampaigns.orderTimeFrom') }}</label>
                  <el-time-picker v-model="model.order_time_from" format="HH:mm" value-format="HH:mm:ss" style="width: 100%;" />
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>{{ $t('challengeCampaigns.orderTimeTo') }}</label>
                  <el-time-picker v-model="model.order_time_to" format="HH:mm" value-format="HH:mm:ss" style="width: 100%;" />
                </div>
              </div>
            </div>

            <h6 class="challenge-campaign-form__group-title">{{ $t('challengeCampaigns.catalogGroup') }}</h6>
            <div class="row">
              <div class="col-md-6">
                <searchable-multi-select
                  v-model="productsSelected"
                  label="challengeCampaigns.products"
                  url="/search-order-products"
                  :initial-limit="20"
                />
              </div>
              <div class="col-md-6">
                <searchable-multi-select
                  v-model="categoriesSelected"
                  label="challengeCampaigns.categories"
                  url="/search-categories"
                  :initial-limit="20"
                />
              </div>
            </div>

            <p class="challenge-campaign-form__summary">
              {{ conditionsSummaryText }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import Vue from 'vue'
import { Upload, Select, Option, DatePicker, TimePicker } from 'element-ui'
import PSwitch from 'src/components/UIComponents/Switch.vue'
import SearchableMultiSelect from 'src/components/UIComponents/Inputs/SearchableMultiSelect.vue'
import swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.css'

Vue.use(Upload)
Vue.use(Select)
Vue.use(Option)
Vue.use(DatePicker)
Vue.use(TimePicker)

export default {
  components: { PSwitch, SearchableMultiSelect },
  props: {
    route: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      id: null,
      loading: true,
      locations: [],
      locationIds: [],
      productsSelected: [],
      categoriesSelected: [],
      deliveryMethodOptions: ['PICK_UP', 'DELIVERY', 'MEET_HALF', 'EAT_LOCATION', 'SELF_ORDERING'],
      sourceOptions: [
        { id: 1, label: 'Kiosk' },
        { id: 2, label: 'QR' },
        { id: 3, label: 'POS' },
        { id: 4, label: 'App' },
        { id: 5, label: 'Website' }
      ],
      dayOptions: [
        { id: 0, label: this.$t('challengeCampaigns.daySun') },
        { id: 1, label: this.$t('challengeCampaigns.dayMon') },
        { id: 2, label: this.$t('challengeCampaigns.dayTue') },
        { id: 3, label: this.$t('challengeCampaigns.dayWed') },
        { id: 4, label: this.$t('challengeCampaigns.dayThu') },
        { id: 5, label: this.$t('challengeCampaigns.dayFri') },
        { id: 6, label: this.$t('challengeCampaigns.daySat') }
      ],
      model: {
        title: '',
        description: '',
        target: 1,
        min_order_value: null,
        period_of_time_days: null,
        distinct_order: false,
        active: true,
        fromto: [],
        order_time_from: null,
        order_time_to: null,
        delivery_methods: [],
        order_source_ids: [],
        days: [],
        imagelist: [],
        image: null
      }
    }
  },
  computed: {
    currency () {
      const company = this.$session.get('selectedCompanyData') || {}
      return company.currency_iso || ''
    },
    conditionsSummaryText () {
      return this.buildConditionsSummary()
    }
  },
  created () {
    this.id = (this.route && this.route.params && this.route.params.id) || (this.$route.params && this.$route.params.id) || null
    this.getLocations().then(() => {
      if (this.id) this.getCampaign()
      else this.loading = false
    }).catch(() => { this.loading = false })
  },
  methods: {
    formatList (items) {
      return (items || []).filter(Boolean).join(', ')
    },
    shortTime (value) {
      if (!value) return ''
      return String(value).substring(0, 5)
    },
    buildConditionsSummary () {
      const parts = []
      const target = parseInt(this.model.target, 10) || 1
      const hasProducts = (this.productsSelected || []).length > 0
      const hasCategories = (this.categoriesSelected || []).length > 0
      const membershipOnly = !!(this.model.period_of_time_days) && !hasProducts && !hasCategories

      if (membershipOnly) {
        parts.push(this.$t('challengeCampaigns.summaryMembership', {
          days: this.model.period_of_time_days,
          target: target
        }))
      } else if (this.model.distinct_order || (!hasProducts && !hasCategories)) {
        parts.push(this.$t('challengeCampaigns.summaryOrders', { target: target }))
      } else {
        parts.push(this.$t('challengeCampaigns.summaryProductQty', { target: target }))
      }

      if (this.model.min_order_value) {
        parts.push(this.$t('challengeCampaigns.summaryMinOrder', {
          amount: this.model.min_order_value,
          currency: this.currency
        }))
      }

      if ((this.locationIds || []).length) {
        const names = this.locations
          .filter(l => this.locationIds.indexOf(l.id) !== -1)
          .map(l => l.name)
        parts.push(this.$t('challengeCampaigns.summaryLocations', { list: this.formatList(names) }))
      }

      if ((this.model.delivery_methods || []).length) {
        const names = this.model.delivery_methods.map(m => this.$t('deliveryTypes.' + m))
        parts.push(this.$t('challengeCampaigns.summaryDelivery', {
          list: this.formatList(names)
        }))
      }

      if ((this.model.order_source_ids || []).length) {
        const names = this.sourceOptions
          .filter(s => this.model.order_source_ids.indexOf(s.id) !== -1)
          .map(s => s.label)
        parts.push(this.$t('challengeCampaigns.summarySources', { list: this.formatList(names) }))
      }

      if ((this.model.days || []).length) {
        const names = this.dayOptions
          .filter(d => this.model.days.indexOf(d.id) !== -1)
          .map(d => d.label)
        parts.push(this.$t('challengeCampaigns.summaryDays', { list: this.formatList(names) }))
      }

      if (this.model.order_time_from || this.model.order_time_to) {
        parts.push(this.$t('challengeCampaigns.summaryOrderTime', {
          from: this.shortTime(this.model.order_time_from) || '—',
          to: this.shortTime(this.model.order_time_to) || '—'
        }))
      }

      if (this.model.fromto && this.model.fromto[0] && this.model.fromto[1]) {
        parts.push(this.$t('challengeCampaigns.summaryPeriod', {
          from: this.model.fromto[0],
          to: this.model.fromto[1]
        }))
      }

      if (hasProducts) {
        const names = (this.productsSelected || []).map(p => p.label || p.name || ('#' + p.id))
        parts.push(this.$t('challengeCampaigns.summaryProducts', { list: this.formatList(names) }))
      }

      if (hasCategories) {
        const names = (this.categoriesSelected || []).map(c => c.label || c.name || ('#' + c.id))
        parts.push(this.$t('challengeCampaigns.summaryCategories', { list: this.formatList(names) }))
      }

      if (this.model.period_of_time_days && !membershipOnly) {
        parts.push(this.$t('challengeCampaigns.summaryWithinDays', {
          days: this.model.period_of_time_days
        }))
      }

      return parts.join(' ')
    },
    getLocations () {
      return new Promise((resolve, reject) => {
        const data = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
        this.apiRequest('/get-locations', 'GET', data, (response) => {
          if (response.status) {
            this.locations = response.data || []
            resolve()
          } else reject(new Error('locations'))
        })
      })
    },
    getCampaign () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/challenge-campaigns/' + this.id, 'GET', data, (response) => {
        if (response.status && response.data) {
          const item = response.data
          this.model.title = item.title
          this.model.description = item.description
          this.model.target = item.target
          this.model.min_order_value = item.min_order_value
          this.model.period_of_time_days = item.period_of_time_days
          this.model.distinct_order = !!item.distinct_order
          this.model.active = !!item.active
          this.model.delivery_methods = item.delivery_methods || []
          this.model.order_source_ids = item.order_source_ids || []
          this.model.days = item.days || []
          this.model.order_time_from = item.order_time_from
          this.model.order_time_to = item.order_time_to
          if (item.time_from && item.time_to) {
            this.model.fromto = [item.time_from, item.time_to]
          }
          if (item.image) {
            this.model.imagelist = [{ url: item.image }]
            this.model.image = item.image
          }
          this.locationIds = item.location_ids || []
          if (Array.isArray(item.products) && item.products.length) {
            this.productsSelected = item.products.map(p => ({
              id: p.id,
              label: p.label || p.name || ('#' + p.id)
            }))
          } else {
            this.productsSelected = (item.product_ids || []).map(id => ({ id, label: '#' + id }))
          }
          if (Array.isArray(item.categories) && item.categories.length) {
            this.categoriesSelected = item.categories.map(c => ({
              id: c.id,
              label: c.label || c.name || ('#' + c.id)
            }))
          } else {
            this.categoriesSelected = (item.category_ids || []).map(id => ({ id, label: '#' + id }))
          }
        }
        this.loading = false
      })
    },
    validate () {
      this.$validator.validateAll().then(isValid => {
        if (isValid) this.saveCampaign()
      })
    },
    saveCampaign () {
      this.loading = true
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        title: this.model.title,
        description: this.model.description,
        target: this.model.target || 1,
        min_order_value: this.model.min_order_value || null,
        period_of_time_days: this.model.period_of_time_days || null,
        distinct_order: this.model.distinct_order ? 1 : 0,
        active: this.model.active ? 1 : 0,
        location_ids: this.locationIds || [],
        delivery_methods: this.model.delivery_methods || [],
        order_source_ids: this.model.order_source_ids || [],
        days: this.model.days || [],
        product_ids: (this.productsSelected || []).map(p => p.id),
        category_ids: (this.categoriesSelected || []).map(c => c.id),
        order_time_from: this.model.order_time_from || null,
        order_time_to: this.model.order_time_to || null,
        time_from: (this.model.fromto && this.model.fromto[0]) ? this.model.fromto[0] : null,
        time_to: (this.model.fromto && this.model.fromto[1]) ? this.model.fromto[1] : null
      }
      if (this.model.image && String(this.model.image).indexOf('http') !== 0) {
        data.image = this.model.image
      } else if (this.model.image && String(this.model.image).indexOf('data:') === 0) {
        data.image = this.model.image
      } else if (Array.isArray(this.model.imagelist) && this.model.imagelist.length === 0) {
        data.image = ''
      } else if (this.model.image) {
        data.image = this.model.image
      }

      const url = this.id ? '/challenge-campaigns/' + this.id : '/challenge-campaigns'
      const method = this.id ? 'PUT' : 'POST'
      this.apiRequest(url, method, data, (response) => {
        this.loading = false
        if (response.status) {
          swal({
            title: '',
            text: this.id ? this.$t('challengeCampaigns.campaignUpdated') : this.$t('challengeCampaigns.campaignAdded'),
            type: 'success',
            showConfirmButton: false,
            timer: 1500
          }).catch(() => {})
          this.backButton()
        } else if (response.errors) {
          Object.keys(response.errors).forEach(k => {
            const msg = Array.isArray(response.errors[k]) ? response.errors[k][0] : response.errors[k]
            this.errors.add(k, msg)
          })
        } else {
          swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
        }
      })
    },
    deleteCampaign () {
      swal({
        text: this.$t('challengeCampaigns.areYouSureDelete') + ' ' + (this.model.title || '') + '?',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes')
      }).then((result) => {
        if (!result) return
        const data = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
        this.apiRequest('/challenge-campaigns/' + this.id, 'DELETE', data, (response) => {
          if (response.status) this.backButton()
          else swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
        })
      }).catch(() => {})
    },
    handleImgChange (file, fileList) {
      if (file.size / 1024 / 1024 >= 5) {
        this.$refs.upload.clearFiles()
        this.model.imagelist = []
        return
      }
      this.model.imagelist = fileList.length > 1 ? fileList.slice(1) : fileList
      if (this.model.imagelist[0] && this.model.imagelist[0].raw) {
        this.getBase64(this.model.imagelist[0].raw).then((base64enc) => {
          this.model.image = base64enc
        })
      }
    },
    handleRemove (file, fileList) {
      if (!fileList.length) {
        this.model.imagelist = []
        this.model.image = null
      }
    },
    handleExceed () {
      swal({ text: this.$t('challengeCampaigns.removeCurrentImage'), type: 'warning' })
    },
    backButton () {
      this.$bus.$emit('refresh')
      this.$bus.$emit('close-modal')
    }
  }
}
</script>

<style scoped>
.challenge-campaign-form {
  position: relative;
}
.challenge-campaign-form__loader {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 20;
}
.challenge-campaign-form__section-title {
  margin: -5px 0 12px !important;
}
.challenge-campaign-form__section-title h5 {
  margin: 0;
  font-weight: 600;
}
.challenge-campaign-form__group-title {
  margin: 18px 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: #65739A;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.challenge-campaign-form__info-list {
  margin: -4px 0 14px;
  padding-left: 18px;
  color: #65739A;
  font-size: 12px;
  line-height: 1.45;
}
.challenge-campaign-form__info-list li {
  margin-bottom: 4px;
}
.challenge-campaign-form__active {
  display: flex;
  align-items: center;
  gap: 12px;
}
.challenge-campaign-form__active label {
  margin: 0;
}
.challenge-campaign-form__switch-field small {
  display: block;
  margin-top: 4px;
  line-height: 1.3;
  font-size: 11px;
}
.challenge-campaign-form .form-group {
  margin-bottom: 12px;
}
.challenge-campaign-form__summary {
  margin: 18px 0 0;
  padding: 12px 14px;
  border-radius: 4px;
  background: #f7f9fc;
  border-left: 3px solid #51cbce;
  color: #333;
  line-height: 1.45;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/ChallengeCampaigns/ChallengeCampaignForm.vue